/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-underscore-dangle */

/**
 * Language management page
 */
import MDBox from "components/Basics/MDBox";
import DashboardLayout from "components/Advanced/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Advanced/Navbars/DashboardNavbar";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ChartsActions from "redux-react/actions/chartsActions";
import FormActions from "redux-react/actions/formAction";
import { Icon, IconButton, Tooltip } from "@mui/material";
import i18n from "i18n";
import { useMaterialUIController } from "context";
import ChartsLoader from "components/Custom/ChartsLoader";
import DefaultDataTable from "components/Custom/Tables/DefaultDataTable";
import MDButton from "components/Basics/MDButton";
import { display } from "redux-react/reducers/snackBarReducer";

import { getLocalStorageBackValues } from "components/Custom/Filters/filters";
import { socket } from "redux-react/middleware/ws";
import ConfirmDialogButton from "components/Custom/Dialogs/ConfirmDialogButton";
import LanguageDialog from "./components/LanguageDialog";
import { t } from "i18next";

/**
 *
 * @param {Object} route
 *  - key : actual page
 *  - type : type of page
 *  - icon : icon to display in navbar
 *  - route : actual route
 * @returns
 */
export default function LanguagePage({ route }) {
	const dispatch = useDispatch();

	const { profile, filters } = useSelector(state => state);
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;
	// Loader while charts are loading
	const [chartsLoading, setChartsLoading] = useState(false);
	// Raw charts from collection "analytics" in DB
	const [rawCharts, setRawCharts] = useState({});
	// Raw charts builded from the back
	const [chartsData, setChartsData] = useState({});
	// Default assistant filters
	const [pageFilters, setPageFilters] = useState([]);
	const [reloadTable, setReloadTable] = useState(false);

	const [languageDialogState, setLanguageDialogState] = useState({
		open: false,
		languages: [],
		assistantLanguages: []
	});

	const closeFormAddLanguage = () => {
		setLanguageDialogState({ open: false, languages: [], assistantLanguages: [] });
	};

	const languageAddActionHandle = () => {
		const onSuccess = res => {
			setLanguageDialogState({
				open: true,
				languages: res.languagesFromCatalog,
				assistantLanguages: res.languagesFromAssistant
			});
		};
		dispatch(FormActions.getLanguagesAddForm(onSuccess, () => {}));
	};

	const languageAddNewHandle = language => {
		const onSuccess = res => {
			closeFormAddLanguage();
		};

		language.default = false;
		language.assistantID = profile.assistantID;

		let data = { values: language, target: "language", unique: {} };
		dispatch(
			FormActions.addItemEmpty(data, onSuccess, err => {
				dispatch(
					display({
						message: t("FORMS.language.cannotAddLanguage"),
						type: "error"
					})
				);
			})
		);
	};

	const actionLanguageDefault = values => {
		const onSuccess = res => {
			setReloadTable(!reloadTable);
		};
		values.default = true;
		let data = { values, target: "language", unique: {} };
		dispatch(
			FormActions.updateItem(values._id, data, onSuccess, err => {
				dispatch(
					display({
						message: t("FORMS.language.cannotSetDefault"),
						type: "error"
					})
				);
			})
		);
	};

	/**
	 * Get charts data to be displayed in front
	 */
	function getChartsData() {
		return chartsData[route.route] ?? [];
	}

	/**
	 * FIRST STEP : Get raw charts from DB
	 */
	function getRawChartsFromDatabase() {
		const onSuccess = res => {
			setPageFilters(res.filters);
			setRawCharts({ ...rawCharts, [route.route]: res.charts });
		};
		dispatch(ChartsActions.getCharts(profile.assistantID, route, onSuccess));
	}

	const getMoreFilters = () => {
		return getLocalStorageBackValues(profile.selectedAssistant.assistantID, route.route, filters);
	};

	/**
	 * SECOND STEP : Build charts with the raw charts on the back
	 */
	function buildRawCharts() {
		// Success handler : set charts data

		const onSuccess = res => {
			setChartsData({ ...chartsData, [route.route]: res.charts });
			setChartsLoading(false);
		};
		// Build charts
		if (!rawCharts[route.route]) return;
		let mandatoryFilters = pageFilters.map(filter => filter.attribute);

		dispatch(
			ChartsActions.buildRawCharts(rawCharts[route.route], filters, mandatoryFilters, onSuccess)
		);
	}
	/**
	 * FIRST STEP : Get raw charts :
	 * - On mount / when page change
	 * - On selected assistant change
	 */
	useEffect(() => {
		setChartsLoading(true);
		getRawChartsFromDatabase();
	}, [profile.selectedAssistant.assistantID, route]);
	/**
	 * SECOND STEP : Build charts :
	 * - On filters change
	 * - On rawCharts change
	 */
	useEffect(() => {
		buildRawCharts();
	}, [rawCharts]);

	const refreshCharts = ({ success }) => {
		dispatch(
			display({
				message: success ? i18n.t("FORMS.addEmpty") : i18n.t("FORMS.notSuccess"),
				type: success ? "success" : "error"
			})
		);
		setReloadTable(!reloadTable);
	};

	const handleDelete = item => {
		const onSuccess = res => {
			dispatch(
				display({
					message: i18n.t("FORMS.deleteSuccess"),
					type: "success"
				})
			);
			setReloadTable(!reloadTable);
		};
		dispatch(
			FormActions.deleteItem(
				item._id,
				"language",
				{
					delete: true
				},
				onSuccess
			)
		);
	};

	useEffect(() => {
		socket.on(`updatePageChart_${route?.form?.collectionDefault}`, refreshCharts);

		return () => {
			socket.off(`updatePageChart_${route?.form?.collectionDefault}`, refreshCharts);
		};
	}, [profile.selectedAssistant.assistantID, route, reloadTable]);

	/**
	 * Charts loader
	 */
	if (chartsLoading) {
		return (
			<DashboardLayout>
				<MDBox py={3}>
					<DashboardNavbar />
					<ChartsLoader darkMode={darkMode} />
				</MDBox>
			</DashboardLayout>
		);
	} else
		/**
		 * Main component
		 */
		return (
			<DashboardLayout>
				<MDBox mb={3}>
					<DashboardNavbar
						filters={[
							<MDBox display="flex" alignItems="center">
								<MDButton
									style={{ height: "100%", marginRight: "0.75rem" }}
									variant="contained"
									color="info"
									onClick={languageAddActionHandle}
								>
									<Icon>add</Icon>&nbsp;Ajouter une langue
								</MDButton>
							</MDBox>
						]}
					/>
				</MDBox>

				{/* Form V2 Dialog */}
				<LanguageDialog
					{...languageDialogState}
					onClose={closeFormAddLanguage}
					onSave={languageAddNewHandle}
				/>

				{/*
				 * Pagined table
				 */}
				{getChartsData()
					.filter(chart => chart.type === "paginedList")
					.map((chart, index) => {
						const chartFilters = getMoreFilters();
						return (
							<DefaultDataTable
								key={index}
								form={route.form}
								dictionary={chart.dictionary}
								canSearch
								table={chart.data}
								display={chart.request.attributesDisplay}
								pagination={chart.pagination}
								list={chart}
								reloadTable={reloadTable}
								filters={chartFilters}
								actions={({ original }) => {
									return (
										<MDBox display="flex" alignItems="center">
											<Tooltip title={t("FORMS.language.setDefault")} placement="top">
												<IconButton
													disabled={original.default === true}
													onClick={({ values }, e) => {
														actionLanguageDefault(original);
													}}
												>
													<Icon fontSize="medium">check</Icon>
												</IconButton>
											</Tooltip>

											<ConfirmDialogButton
												onConfirm={(e, values) => {
													handleDelete(original);
												}}
												component={
													<Tooltip title={t("FORMS.language.deleteLanguage")} placement="top">
														<IconButton color="error">
															<Icon fontSize="medium">delete</Icon>
														</IconButton>
													</Tooltip>
												}
												// rowValues={original}
												title={`Retirer la langue ${original.name}`}
												content="Cette langue ne sera plus disponible dans les assistants. Vous pourrez la réimporter à tout moment. Voulez-vous continuer ?"
												// displayAttribute="name"
											/>
										</MDBox>
									);
								}}
							/>
						);
					})}
			</DashboardLayout>
		);
}
